import type { BaselineWeekCounts } from '@galapagos/shared';
import { ordinalOnWeekday, reportedDays, WEEKDAYS, type Weekday } from './baseline-days';

/** The most a member can enter for one day, as Galapagos onboarding allows. */
export const MAX_DAY_COUNT = 30;

/** A week's counts by weekday. */
export type WeekdayCounts = ReadonlyMap<Weekday, number>;

/** Day-order counts (index 0 is day 1) regrouped by the weekday each day fell on. */
export function countsByWeekday(
  anchorDate: string,
  zone: string,
  counts: BaselineWeekCounts,
): WeekdayCounts {
  return new Map(
    reportedDays(anchorDate, zone).map((day) => [day.weekday, counts[day.ordinal - 1] ?? 0]),
  );
}

/**
 * Weekday counts back in day order, aligned so day N's slot holds the count
 * for the weekday that fell N days before the anchor.
 */
export function countsInDayOrder(
  anchorDate: string,
  zone: string,
  byWeekday: WeekdayCounts,
): number[] {
  const counts = Array.from({ length: WEEKDAYS.length }, () => 0);
  for (const weekday of WEEKDAYS) {
    counts[ordinalOnWeekday(anchorDate, zone, weekday) - 1] = byWeekday.get(weekday) ?? 0;
  }
  return counts;
}

export interface WeekSummaryCell {
  weekday: Weekday;
  count: number;
}

export interface WeekSummaryData {
  total: number;
  /** Days with a count of zero. */
  freeDays: number;
  /** Monday to Sunday. */
  cells: WeekSummaryCell[];
}

export function weekSummary(byWeekday: WeekdayCounts): WeekSummaryData {
  const cells = WEEKDAYS.map((weekday) => ({ weekday, count: byWeekday.get(weekday) ?? 0 }));
  return {
    total: cells.reduce((sum, cell) => sum + cell.count, 0),
    freeDays: cells.filter((cell) => cell.count === 0).length,
    cells,
  };
}
